// PRPathways.js - New Zealand
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, MapPin, Users, ChevronDown, ChevronUp } from 'lucide-react';

const PRPathways = () => {
  const [expandedPathway, setExpandedPathway] = useState(null);

  const pathways = [
    {
      icon: Zap,
      title: 'Skilled Migrant Category (SMC)',
      subtitle: 'The points-tested residence visa for skilled workers',
      description: 'The route most skilled workers take. Your score is built from age, qualifications, work history and any job offer you hold.',
      details: {
        overview: 'SMC is the principal residence category for skilled workers in New Zealand. You enter a pool by lodging an Expression of Interest (EOI), and selections are drawn from that pool according to points.',
        programs: [
          {
            name: 'Skilled Migrant Category Resident Visa',
            requirements: 'Entry to the pool starts at 160 points, with selection generally happening from 180 points upwards. Age scores best between 20 and 39, a Level 7 or higher qualification is worth 40-70 points, skilled employment carries 50-60 points, and New Zealand experience counts as well. You need a skilled job or job offer in the country, plus English at IELTS 6.5 overall or the equivalent.'
          },
          {
            name: 'Points Breakdown',
            requirements: 'Age 20-39 earns 30 points. A bachelor degree earns 40 points, a master\'s 50 and a PhD 70. Skilled employment in New Zealand adds 50 points. Your partner\'s qualifications or employment can contribute up to 20. A year or more of New Zealand work experience adds 10 points, and a qualification in an area of absolute skills shortage adds a further 10.'
          },
          {
            name: 'Job Offer Requirements',
            requirements: 'The role has to be skilled, meaning ANZSCO skill levels 1-3, genuine, full time at 30 hours a week or more, and paid at the market rate. Your employer must either hold accreditation or otherwise show the need is real. An offer lifts your chance of selection considerably.'
          }
        ],
        keyPoints: [
          'The pool opens at 160 points; selection normally begins at 180',
          'Draws from the pool happen regularly, roughly every 2 weeks',
          'An offer of employment in New Zealand is strongly advisable',
          'English has to reach IELTS 6.5 overall at minimum',
          'Once you are invited, processing takes 6-12 months',
          'Your partner and dependent children can be included'
        ]
      }
    },
    {
      icon: MapPin,
      title: 'Regional Pathways',
      subtitle: 'Work to Residence and other options outside Auckland',
      description: 'Alternative routes for people willing to work with an accredited employer or settle away from the main centre.',
      details: {
        overview: 'Employer-led and regional routes exist for applicants who would struggle on points alone, and requirements are often lighter if you are prepared to live outside Auckland.',
        programs: [
          {
            name: 'Work to Residence (Accredited Employer)',
            requirements: 'Begins with a job offer in a skilled occupation from an accredited employer. You hold a work visa for 30 months and can then apply for residence. No points test is involved. Health, character and English requirements still apply, and the salary has to meet the median wage threshold.'
          },
          {
            name: 'Straight to Residence (Accredited Employer)',
            requirements: 'Open to highly skilled roles, typically ANZSCO level 1-2, paying NZD $106,080 or above. With an offer from an accredited employer you apply for residence directly and skip the work visa stage altogether. It is the faster route for high earners.'
          },
          {
            name: 'Regional Skilled Migration',
            requirements: 'Points requirements ease for applicants holding job offers in regions outside Auckland, and regional employment brings extra points. You commit to working in that region for a set period. The purpose is to fill shortages away from the main centre.'
          }
        ],
        keyPoints: [
          'Work to Residence involves no points test at all',
          'Your employer has to hold Immigration NZ accreditation',
          'Salary must meet or exceed the median wage threshold',
          'Working regionally adds points within the SMC',
          'Straight to Residence applies from NZD $106,080 upwards',
          'Depending on the route, expect 6-18 months of processing'
        ]
      }
    },
    {
      icon: Users,
      title: 'Family & Partner Pathways',
      subtitle: 'Partnership, parent and dependent child categories',
      description: 'Residence through a relationship with a New Zealand citizen or resident rather than through employment.',
      details: {
        overview: 'New Zealand citizens and residents may sponsor certain family members for residence, subject to relationship, income and family balance tests.',
        programs: [
          {
            name: 'Partnership Resident Visa',
            requirements: 'For the partner of a New Zealand citizen or resident. You must have lived together in a genuine and stable relationship for 12 months or more, and you can apply from inside or outside the country. The visa comes in two stages, temporary for 2 years and then permanent. Your sponsor has to meet an income requirement of NZD $43,322.76 for a couple.'
          },
          {
            name: 'Parent Resident Visa',
            requirements: 'For the parent of an adult, settled New Zealand citizen or resident. The balance of family test applies, so at least as many of your children must live in New Zealand as anywhere else. Income thresholds fall on the sponsor: NZD $65,000+ where one parent applies, NZD $90,000+ for two. Health and character requirements apply throughout.'
          },
          {
            name: 'Dependent Child Resident Visa',
            requirements: 'For a child under 24 whose parent is a New Zealand citizen or resident. The child has to be single and financially dependent on that parent. Adopted children and step-children may be included. Health and character requirements apply.'
          }
        ],
        keyPoints: [
          'Partnership cases turn on the quality of your relationship evidence',
          'Sponsors of parents have to meet an income threshold',
          'The balance of family test governs every parent application',
          'Allow 12-24 months for partnership cases and 24-36 months for parents',
          'Each applicant sits a medical examination',
          'Police certificates are needed for every country lived in for 12+ months'
        ]
      }
    }
  ];

  const togglePathway = (index) => {
    setExpandedPathway(expandedPathway === index ? null : index);
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Ways into New Zealand Residence
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Expand a category below to see the requirements attached to it and how it compares with the alternatives.
          </p>
        </motion.div>

        {/* Pathways */}
        <div className="space-y-6">
          {pathways.map((pathway, index) => {
            const Icon = pathway.icon;
            const isExpanded = expandedPathway === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                {/* Pathway Header */}
                <button
                  onClick={() => togglePathway(index)}
                  className="w-full p-6 flex items-start gap-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={24} className="text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-primary mb-2">
                      {pathway.title}
                    </h3>
                    <p className="text-sm text-gray-500 font-light mb-2">
                      {pathway.subtitle}
                    </p>
                    <p className="text-gray-600 text-sm font-light">
                      {pathway.description}
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp size={24} className="text-secondary" />
                    ) : (
                      <ChevronDown size={24} className="text-secondary" />
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 border-t border-gray-200">
                        <div className="pt-6 space-y-6">
                          {/* Overview */}
                          <div>
                            <h4 className="text-base font-semibold text-primary mb-2">
                              Overview
                            </h4>
                            <p className="text-gray-600 text-sm font-light leading-relaxed">
                              {pathway.details.overview}
                            </p>
                          </div>

                          {/* Programs/Categories */}
                          <div>
                            <h4 className="text-base font-semibold text-primary mb-3">
                              Programs & Requirements
                            </h4>
                            <div className="space-y-4">
                              {pathway.details.programs.map((program, pIndex) => (
                                <div key={pIndex} className="bg-surface p-4 rounded-lg">
                                  <h5 className="text-sm font-semibold text-primary mb-2">
                                    {program.name}
                                  </h5>
                                  <p className="text-gray-600 text-sm font-light leading-relaxed">
                                    {program.requirements}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Key Points */}
                          <div>
                            <h4 className="text-base font-semibold text-primary mb-3">
                              Key Points
                            </h4>
                            <ul className="space-y-2">
                              {pathway.details.keyPoints.map((point, kIndex) => (
                                <li key={kIndex} className="flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0"></span>
                                  <span className="text-gray-600 text-sm font-light">
                                    {point}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PRPathways;
